import { useCallback, useEffect, useState } from 'react'
import logo from './assets/logo.png'
import { Icono } from './componentes/Icono.js'
import type { NombreIcono } from './componentes/Icono.js'
import { obtenerRepositorio } from './datos/index.js'
import type { Jugador } from './datos/index.js'
import { useAvisoDeDesborde } from './desborde.js'
import { EnVivo } from './pantallas/EnVivo.js'
import type { Grupo } from './pantallas/EnVivo.js'
import { Inicio } from './pantallas/Inicio.js'
import { Perfil } from './pantallas/Perfil.js'
import { Clave } from './pantallas/Clave.js'
import { Ingreso } from './pantallas/Ingreso.js'
import { Torneo } from './pantallas/Torneo.js'
import { Anotacion } from './pantallas/Anotacion.js'
import { LibretaPersonal, NuevaPartida, PartidaSuelta } from './pantallas/LibretaPersonal.js'
import { Torneos } from './pantallas/Torneos.js'
import { VitrinaTorneo } from './pantallas/VitrinaTorneo.js'
import { esPublica, ir, reemplazar, useVista } from './navegacion.js'
import type { Vista } from './navegacion.js'
import { alternarTema, useTema } from './tema.js'

/**
 * La web del jugador.
 *
 * Es la tercera pieza del sistema y la única que no escribe nada: la app del
 * salón manda los datos a la nube y esto los muestra. Por eso no hay formularios
 * fuera del ingreso, ni botones que puedan fallar a mitad de camino — lo peor
 * que puede pasar acá es que algo no llegue.
 *
 * La web tiene dos formas. En el teléfono es una app: la navegación es una
 * píldora que flota abajo, al alcance del pulgar, porque se mira parado y con
 * una mano. En una pantalla grande es una web: la navegación sube y entra en la
 * misma barra que el logo, porque con mouse abajo es el peor lugar. Es el mismo
 * `<nav>` en las dos, adentro de la barra; lo que cambia es la hoja de estilo.
 *
 * Hay una parte que se ve sin cuenta: la vitrina, con los torneos del club, sus
 * mesas y su tabla. Es la portada para el que llega sin sesión, con su menú de
 * tres (En vivo, Próximos, Resultados). El que ya entró tiene otro menú (Hoy,
 * Torneos, Perfil) y la vitrina le queda adentro de «Torneos», junto a los
 * suyos.
 */

interface Tab {
  vista: Vista
  icono: NombreIcono
  texto: string
}

const TABS: readonly Tab[] = [
  { vista: { nombre: 'inicio' }, icono: 'inicio', texto: 'Hoy' },
  { vista: { nombre: 'torneos' }, icono: 'torneos', texto: 'Torneos' },
  /* La libreta personal tiene pestaña propia y no cuelga de «Torneos»: lo que
     se anota ahí no es de ningún torneo, y ponerla adentro haría pensar que sí. */
  { vista: { nombre: 'libreta' }, icono: 'libreta', texto: 'Libreta' },
  { vista: { nombre: 'perfil' }, icono: 'persona', texto: 'Perfil' },
]

/* Sin cuenta, el menú es la vitrina partida en sus tres momentos. Es el mismo
   `<nav>` que con sesión: píldora abajo en el teléfono, fila en la barra en una
   pantalla grande. */
const TABS_PUBLICAS: readonly Tab[] = [
  { vista: { nombre: 'vivo' }, icono: 'vivo', texto: 'En vivo' },
  { vista: { nombre: 'proximos' }, icono: 'calendario', texto: 'Próximos' },
  { vista: { nombre: 'resultados' }, icono: 'medalla', texto: 'Resultados' },
]

/** El menú de pestañas, el mismo con y sin sesión. */
function Navegacion({ tabs, activa }: { tabs: readonly Tab[]; activa: string }) {
  return (
    <nav className="tabs" aria-label="Secciones">
      {tabs.map((t) => (
        <button
          key={t.texto}
          className={activa === t.vista.nombre ? 'activa' : ''}
          aria-current={activa === t.vista.nombre ? 'page' : undefined}
          onClick={() => ir(t.vista)}
        >
          <Icono nombre={t.icono} tamano={21} />
          <span>{t.texto}</span>
        </button>
      ))}
    </nav>
  )
}

/**
 * El botón de claro y oscuro. Muestra el tema al que se pasa y no el que está
 * puesto: es lo que hace el botón, y el que está puesto ya se ve.
 */
function BotonTema() {
  const tema = useTema()
  const texto = tema === 'claro' ? 'Pasar al tema oscuro' : 'Pasar al tema claro'
  return (
    <button className="barra-tema" onClick={alternarTema} title={texto} aria-label={texto}>
      <Icono nombre={tema === 'claro' ? 'luna' : 'sol'} tamano={19} />
    </button>
  )
}

/** Qué grupo de la vitrina corresponde a la vista. */
function grupoDe(vista: Vista): Grupo {
  if (vista.nombre === 'proximos') return 'proximos'
  if (vista.nombre === 'resultados') return 'resultados'
  return 'vivo'
}

/** Qué pestaña de abajo se pinta como activa para la vista que se está viendo. */
function tabDe(vista: Vista): string {
  /* El detalle de un torneo se llega desde las tres, así que se queda con
     «Torneos»: es el lugar del que cuelga en la cabeza de cualquiera. Y la
     vitrina entera es un segmento de «Torneos». */
  if (
    vista.nombre === 'torneo' ||
    vista.nombre === 'anotar' ||
    vista.nombre === 'vivo' ||
    vista.nombre === 'proximos' ||
    vista.nombre === 'resultados' ||
    vista.nombre === 'vivo-torneo'
  ) {
    return 'torneos'
  }
  if (vista.nombre === 'libreta-nueva' || vista.nombre === 'libreta-partida') return 'libreta'
  if (vista.nombre === 'ingreso') return 'inicio'
  return vista.nombre
}

/** Lo mismo para el menú sin cuenta. El detalle de un torneo cuelga de En vivo. */
function tabPublicaDe(vista: Vista): string {
  if (vista.nombre === 'proximos' || vista.nombre === 'resultados') return vista.nombre
  return 'vivo'
}

export default function App() {
  const vista = useVista()
  const [jugador, setJugador] = useState<Jugador | null>(null)
  const [origen, setOrigen] = useState<'api' | 'demo' | null>(null)
  const [listo, setListo] = useState(false)

  useAvisoDeDesborde()

  useEffect(() => {
    let vigente = true
    obtenerRepositorio()
      .then(async (repo) => {
        const sesion = await repo.sesion()
        if (!vigente) return
        setOrigen(repo.origen)
        setJugador(sesion)
      })
      .finally(() => {
        if (vigente) setListo(true)
      })
    return () => {
      vigente = false
    }
  }, [])

  /* Con sesión, el formulario de ingreso no tiene nada que hacer: pasa pronto
     al volver atrás después de entrar, o con un enlace viejo. */
  useEffect(() => {
    if (jugador && vista.nombre === 'ingreso') reemplazar({ nombre: 'inicio' })
  }, [jugador, vista.nombre])

  const salir = useCallback(async () => {
    const repo = await obtenerRepositorio()
    await repo.salir()
    setJugador(null)
    ir({ nombre: 'inicio' })
  }, [])

  /* Un parpadeo de la pantalla de ingreso antes de recuperar la sesión sería
     peor que esperar: el que ya entró vería que lo echaron y volvió a entrar. */
  if (!listo) {
    return (
      <div className="arrancando">
        <img src={logo} alt="" width={64} height={64} />
      </div>
    )
  }

  /* El enlace de la contraseña se atiende con o sin sesión: lo abrió alguien
     desde un correo, y puede ser en un teléfono donde ya había otra cuenta. */
  if (vista.nombre === 'clave') {
    return (
      <main className="contenido solo">
        <div className="solo-tema">
          <BotonTema />
        </div>
        <Clave token={vista.token} alIngresar={setJugador} />
      </main>
    )
  }

  /* Sin sesión se ve la vitrina. El ingreso aparece cuando se lo pide, o
     cuando se llega por un enlace a algo propio (una mesa, el historial): ese
     enlace lo mandó un jugador y lo que espera es entrar. */
  if (!jugador) {
    if (vista.nombre === 'ingreso' || (!esPublica(vista) && vista.nombre !== 'inicio')) {
      return (
        <main className="contenido solo">
          <div className="solo-tema">
            <BotonTema />
          </div>
          <Ingreso alIngresar={setJugador} />
        </main>
      )
    }

    return (
      <div className="app publica">
        <header className="barra">
          <button className="barra-inicio" onClick={() => ir({ nombre: 'vivo' })}>
            <img className="barra-logo" src={logo} alt="" width={28} height={28} />
            <span className="barra-marca visible">DomiRoyal</span>
          </button>
          <Navegacion tabs={TABS_PUBLICAS} activa={tabPublicaDe(vista)} />
          {origen === 'demo' && (
            <span className="barra-demo" title="Los datos son de demostración: no salen del servidor">
              <Icono nombre="aviso" tamano={14} />
              demo
            </span>
          )}
          <BotonTema />
          <button className="barra-entrar" onClick={() => ir({ nombre: 'ingreso' })}>
            <Icono nombre="entrar" tamano={17} />
            Entrar
          </button>
        </header>

        <main className="contenido">
          {vista.nombre === 'vivo-torneo' ? (
            <VitrinaTorneo torneoId={vista.id} pestana={vista.pestana} />
          ) : (
            /* La llave hace que cada pestaña arranque de cero: sin ella, pasar
               de En vivo a Próximos mostraría un instante la lista anterior. */
            <EnVivo key={tabPublicaDe(vista)} sinCuenta grupo={grupoDe(vista)} />
          )}
        </main>
      </div>
    )
  }

  const activa = tabDe(vista)

  return (
    <div className="app">
      <header className="barra">
        <img className="barra-logo" src={logo} alt="" width={28} height={28} />
        {/* El nombre del sistema aparece recién en pantalla grande. En el
            teléfono el ancho de la barra se lo lleva el nombre del jugador, que
            es el dato que confirma con qué cuenta está mirando; el título de la
            página ya lo dice la pestaña del navegador. */}
        <span className="barra-marca">DomiRoyal</span>

        <Navegacion tabs={TABS} activa={activa} />

        <span className="barra-nombre">{jugador.nombre}</span>
        {origen === 'demo' && (
          <span className="barra-demo" title="Los datos son de demostración: no salen del servidor">
            <Icono nombre="aviso" tamano={14} />
            demo
          </span>
        )}
        <BotonTema />
        <button className="barra-salir" onClick={salir} title="Cerrar sesión" aria-label="Cerrar sesión">
          <Icono nombre="salir" tamano={19} />
        </button>
      </header>

      <main className="contenido">
        {(vista.nombre === 'inicio' || vista.nombre === 'ingreso') && <Inicio jugador={jugador} />}
        {(vista.nombre === 'vivo' || vista.nombre === 'proximos' || vista.nombre === 'resultados') && (
          <Torneos segmento={grupoDe(vista)} />
        )}
        {vista.nombre === 'vivo-torneo' && (
          <VitrinaTorneo torneoId={vista.id} pestana={vista.pestana} conSesion />
        )}
        {vista.nombre === 'torneos' && <Torneos segmento="tuyos" />}
        {vista.nombre === 'perfil' && <Perfil jugador={jugador} alSalir={salir} />}
        {vista.nombre === 'torneo' && <Torneo torneoId={vista.id} pestana={vista.pestana} />}
        {vista.nombre === 'anotar' && (
          <Anotacion key={vista.mesaId} torneoId={vista.id} mesaId={vista.mesaId} />
        )}
        {vista.nombre === 'libreta' && <LibretaPersonal jugador={jugador} />}
        {vista.nombre === 'libreta-nueva' && <NuevaPartida jugador={jugador} />}
        {vista.nombre === 'libreta-partida' && (
          <PartidaSuelta key={vista.id} jugador={jugador} partidaId={vista.id} />
        )}
      </main>
    </div>
  )
}
